import React, { useState, useRef, useEffect } from 'react';
import IntroductionAudio from '../IntroductionAudio';
import { useVirtualClassAudio } from '../hooks/useVirtualClassAudio';
import aiStudentsImage from '../../../asset/images/home/ai_students.png';
import './VirtualClassPage.css';

const HearPage = ({ page, lesson, onNext, onCompletionChange }) => {
  const flashcards = page.flashcards || [];
  const students = page.students || [];
  const step = page.step || {};
  const config = page.config || {};
  
  const [currentFlashcardIndex, setCurrentFlashcardIndex] = useState(0);
  const [selectedFlashcard, setSelectedFlashcard] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [completedFlashcards, setCompletedFlashcards] = useState(new Set());
  const [passedFlashcards, setPassedFlashcards] = useState(new Set());
  const [attemptedFlashcards, setAttemptedFlashcards] = useState(new Set());
  const [flashcardResult, setFlashcardResult] = useState({});
  const [flashcardAttempts, setFlashcardAttempts] = useState({});
  const [showConfusion, setShowConfusion] = useState(false);
  const [shouldStopAnimations, setShouldStopAnimations] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [playedReminderFlashcards, setPlayedReminderFlashcards] = useState(new Set());
  const [showStepResult, setShowStepResult] = useState(false);
  const [stepAttempt, setStepAttempt] = useState(1);
  const [stepPassed, setStepPassed] = useState(false);
  const [borderAnimation, setBorderAnimation] = useState(null);
  
  const animationTimeoutRef = useRef(null);
  const attemptNumberRef = useRef({});
  const studentAudioRef = useRef(null);
  
  const {
    isRecording,
    recordingTime,
    isProcessing,
    isPlayingReminder,
    handleStartRecording,
    handleSubmitRecording,
    playUserRecording,
    playReminderAudio: playReminderAudioHook
  } = useVirtualClassAudio({
    page,
    lesson,
    selectedFlashcard,
    exerciseType: 'hear',
    maxRecordingTime: config.recordingTimeLimit || 15,
    onResult: handleResult,
    getAttemptNumber: (flashcardId) => attemptNumberRef.current[flashcardId] || 0
  });

  const playStudentResponseAudio = (audioData, onEnded) => {
    try {
      if (!audioData) {
        if (onEnded) onEnded();
        return;
      }

      let audioUrl;
      
      if (typeof audioData === 'string' && (audioData.startsWith('http://') || audioData.startsWith('https://'))) {
        audioUrl = audioData;
      } else if (typeof audioData === 'string' && audioData.length > 0) {
        try {
          const audioBytes = Uint8Array.from(atob(audioData), c => c.charCodeAt(0));
          const audioBlob = new Blob([audioBytes], { type: 'audio/wav' });
          audioUrl = URL.createObjectURL(audioBlob);
        } catch (base64Error) {
          console.error('Error decoding base64 audio:', base64Error);
          if (onEnded) onEnded();
          return;
        }
      } else {
        if (onEnded) onEnded();
        return;
      }
      
      if (studentAudioRef.current) {
        studentAudioRef.current.pause();
        studentAudioRef.current.src = '';
      }
      
      const audio = new Audio(audioUrl);
      studentAudioRef.current = audio;
      setIsPlayingAudio(true);
      
      audio.onended = () => {
        setIsPlayingAudio(false);
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
        if (onEnded) onEnded();
      };
      
      audio.onerror = (error) => {
        console.error('Error playing audio:', error);
        setIsPlayingAudio(false);
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
        if (onEnded) onEnded();
      };
      
      audio.play().catch(error => {
        console.error('Error starting audio playback:', error);
        setIsPlayingAudio(false);
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
        if (onEnded) onEnded();
      });
    } catch (error) {
      console.error('Error playing student response audio:', error);
      setIsPlayingAudio(false);
      if (onEnded) onEnded();
    }
  };

  function handleResult(result, userRecordingUrl) {
    const flashcardId = selectedFlashcard.id;
    const currentAttempt = attemptNumberRef.current[flashcardId] || 0;
    const newAttempt = result.attemptNumber !== undefined ? result.attemptNumber : (currentAttempt + 1);
    
    attemptNumberRef.current[flashcardId] = newAttempt;
    setFlashcardAttempts(prev => ({ ...prev, [flashcardId]: newAttempt }));
    
    if (result.passed || result.is_correct) {
      setFlashcardResult(prev => ({ ...prev, [flashcardId]: 'correct' }));
      setBorderAnimation('green');
      setTimeout(() => {
        setBorderAnimation(null);
      }, 5000);
      
      setIsPlayingAudio(true);
      setShouldStopAnimations(true);
      
      
      const hasCustomResponse = selectedFlashcard?.customResponse?.hear;
      
      const flashcardCustomAudio = selectedFlashcard?.customResponse?.hear?.url || 
                                   selectedFlashcard?.customResponse?.hear?.audio?.url || 
                                   selectedFlashcard?.customResponse?.hear?.audio;
      const backendCustomAudio = (hasCustomResponse && (result.student_response_audio || result.studentResponseAudio)) || null;
      const customResponseAudio = flashcardCustomAudio || backendCustomAudio;
      
    
      setPassedFlashcards(prev => {
        const newPassedSet = new Set([...prev, flashcardId]);
        
        setCompletedFlashcards(prevCompleted => {
          const newCompletedSet = new Set([...prevCompleted, flashcardId]);
          const allCompleted = newCompletedSet.size === flashcards.length;
          
          if (allCompleted) {
            checkCompletion(newCompletedSet, newPassedSet);
          }
          
         
          const navigateToNext = () => {
            if (!allCompleted) {
              setTimeout(() => {
                handleNextFlashcard();
              }, 1500); 
            }
          };
          
          if (customResponseAudio) {
            playStudentResponseAudio(customResponseAudio, () => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(true);
              if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
              }
              navigateToNext();
            });
          } else {
            playUserRecording(() => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(true);
              if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
              }
              navigateToNext();
            });
          }
          
          return newCompletedSet;
        });
        
        return newPassedSet;
      });
    } else {
      setFlashcardResult(prev => ({ ...prev, [flashcardId]: 'incorrect' }));
      setBorderAnimation('red');
      setTimeout(() => {
        setBorderAnimation(null);
      }, 3000);
      
      setAttemptedFlashcards(prev => new Set([...prev, flashcardId]));
      
      const isMaxAttemptsReached = newAttempt >= 2;
      const isDone = isMaxAttemptsReached;
      const shouldMoveToNext = isMaxAttemptsReached;
      
      if (isDone) {
        setCompletedFlashcards(prev => {
          const newSet = new Set([...prev, flashcardId]);
          checkCompletion(newSet);
          return newSet;
        });
      }
      
      const customResponseOnWrong = selectedFlashcard?.customResponse?.hear?.onWrong;
      const hasOnWrongResponse = customResponseOnWrong && (
        customResponseOnWrong.audioType !== 'empty' && 
        customResponseOnWrong.audioType !== undefined
      );
      
      if (hasOnWrongResponse) {
        setIsPlayingAudio(true);
        setShouldStopAnimations(false);
        
        const audioType = customResponseOnWrong.audioType || 
                          (customResponseOnWrong.customAudio === 'empty' ? 'empty' : 
                           (customResponseOnWrong.url ? 'url' : 'default'));
        
        const customResponseAudio = audioType === 'url' ? (customResponseOnWrong.url || customResponseOnWrong.audio?.url) : null;
        const ttsText = audioType === 'tts' ? customResponseOnWrong.ttsText : null;
        
        const backendCustomAudio = (customResponseOnWrong && (result.student_response_audio || result.studentResponseAudio)) || null;
        
        if (audioType === 'empty') {
          setIsPlayingAudio(false);
          setShouldStopAnimations(false);
          
          const reminderAudio = getReminderAudio(selectedFlashcard);
          const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
          const shouldPlay = reminderAudio && 
            (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
          
          if (shouldPlay) {
            playReminderAudioHook(reminderAudio);
            
            if (isFlashcardSpecific) {
              setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
            }
          }
        } else if (audioType === 'url' && customResponseAudio) {
          playStudentResponseAudio(customResponseAudio, () => {
            setIsPlayingAudio(false);
            setShouldStopAnimations(false);
            
            const reminderAudio = getReminderAudio(selectedFlashcard);
            const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
            const shouldPlay = reminderAudio && 
              (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
            
            if (shouldPlay) {
              playReminderAudioHook(reminderAudio);
              
              if (isFlashcardSpecific) {
                setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
              }
            }
          });
        } else if (audioType === 'tts') {
          if (backendCustomAudio) {
            playStudentResponseAudio(backendCustomAudio, () => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(false);
              
              const reminderAudio = getReminderAudio(selectedFlashcard);
              const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
              const shouldPlay = reminderAudio && 
                (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
              
              if (shouldPlay) {
                playReminderAudioHook(reminderAudio);
                
                if (isFlashcardSpecific) {
                  setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
                }
              }
            });
          } else {
            setIsPlayingAudio(false);
            setShouldStopAnimations(false);
            
            const reminderAudio = getReminderAudio(selectedFlashcard);
            const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
            const shouldPlay = reminderAudio && 
              (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
            
            if (shouldPlay) {
              playReminderAudioHook(reminderAudio);
              
              if (isFlashcardSpecific) {
                setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
              }
            }
          }
        } else {
          playUserRecording(() => {
            setIsPlayingAudio(false);
            setShouldStopAnimations(false);
            
            const reminderAudio = getReminderAudio(selectedFlashcard);
            const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
            const shouldPlay = reminderAudio && 
              (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
            
            if (shouldPlay) {
              playReminderAudioHook(reminderAudio);
              
              if (isFlashcardSpecific) {
                setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
              }
            }
          });
        }
      } else {
        playUserRecording(() => {
          const reminderAudio = getReminderAudio(selectedFlashcard);
          const isFlashcardSpecific = isFlashcardSpecificReminder(selectedFlashcard);
          const shouldPlay = reminderAudio && 
            (isFlashcardSpecific ? !playedReminderFlashcards.has(flashcardId) : true);
          
          if (shouldPlay) {
            playReminderAudioHook(reminderAudio);
            
            if (isFlashcardSpecific) {
              setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
            }
          }
        });
      }
      
      const updatedCompletedSet = new Set([...completedFlashcards, flashcardId]);
      const allCompleted = updatedCompletedSet.size === flashcards.length;
      
      if (shouldMoveToNext && !allCompleted) {
        const delay = 3000;
        
        setTimeout(() => {
          handleNextFlashcard();
        }, delay);
      }
    }
  }

  const getReminderAudio = (flashcard) => {
    
    if (flashcard?.reminderAudio?.hear) {
      const hearReminder = flashcard.reminderAudio.hear;
    
      if (hearReminder?.url || (typeof hearReminder === 'string' && hearReminder)) {
        return hearReminder;
      }
    }
    
    if (step?.reminderAudio) {
      return step.reminderAudio;
    }
    return null;
  };

  const isFlashcardSpecificReminder = (flashcard) => {
    return !!flashcard?.reminderAudio?.hear;
  };

  const checkCompletion = (completedSet, passedFlashcardsSet = null) => {
    if (completedSet.size === flashcards.length) {
      const totalFlashcards = flashcards.length;
      const passedSet = passedFlashcardsSet || passedFlashcards;
      const passedCount = Array.from(passedSet).length;
      const percentage = totalFlashcards > 0 ? Math.round((passedCount / totalFlashcards) * 100) : 0;
      const passThreshold = totalFlashcards === 3 ? 66 : (totalFlashcards === 5 ? 80 : (config.passingScore || 80));
      const passed = percentage >= passThreshold;
      
      setStepPassed(passed);
      setShowStepResult(true);
      
      if (onCompletionChange) {
        onCompletionChange(passed);
      }
    }
  };

  const handleFlashcardClick = (flashcard, index) => {
    const canClick = index === currentFlashcardIndex || 
                    (index === currentFlashcardIndex + 1 && attemptedFlashcards.has(flashcards[currentFlashcardIndex]?.id));
    
    if (canClick) {
      setSelectedFlashcard(flashcard);
      setCurrentFlashcardIndex(index);
    }
  };

  const canClickFlashcard = (index) => {
    if (index === currentFlashcardIndex) {
      return true;
    }
    if (index === currentFlashcardIndex + 1) {
      const currentFlashcardId = flashcards[currentFlashcardIndex]?.id;
      return completedFlashcards.has(currentFlashcardId);
    }
    return false;
  };

  const handleStart = () => {
    setIsStarted(true);
    setCurrentFlashcardIndex(0);
    setSelectedFlashcard(flashcards[0]);
    setCompletedFlashcards(new Set());
    setPassedFlashcards(new Set());
    setAttemptedFlashcards(new Set());
    setFlashcardAttempts({});
    attemptNumberRef.current = {};
    setFlashcardResult({});
    
    if (onCompletionChange) {
      onCompletionChange(false);
    }
  };

  useEffect(() => {
    if (!isStarted || completedFlashcards.size < flashcards.length) {
      if (onCompletionChange) {
        onCompletionChange(false);
      }
    }
  }, [isStarted, completedFlashcards.size, flashcards.length, onCompletionChange]);

  useEffect(() => {
    if (onCompletionChange) {
      onCompletionChange(false);
    }
  }, []);

  const handleMoveToNext = () => {
    if (onNext) {
      onNext();
    }
  };

  const handleNextFlashcard = () => {
    if (currentFlashcardIndex < flashcards.length - 1) {
      const nextIndex = currentFlashcardIndex + 1;
      setCurrentFlashcardIndex(nextIndex);
      setSelectedFlashcard(null);
      setShowConfusion(false);
      setBorderAnimation(null);
    }
  };

  const handleRetry = () => {
    setShowStepResult(false);
    setCurrentFlashcardIndex(0);
    setSelectedFlashcard(null);
    setCompletedFlashcards(new Set());
    setPassedFlashcards(new Set());
    setAttemptedFlashcards(new Set());
    setFlashcardAttempts({});
    setFlashcardResult({});
    setStepAttempt(prev => prev + 1);
    setPlayedReminderFlashcards(new Set());
    
    if (onCompletionChange) {
      onCompletionChange(false);
    }
  };

  if (!isStarted) {
    return (
      <div className="text-center">
        {page.introductionAudio && (
          <div className="mb-6">
            <IntroductionAudio
              audio={page.introductionAudio}
              autoPlay={page.introductionAudio.autoPlay}
            />
          </div>
        )}
        
        <div className="mb-6">
          <div className="flex justify-center gap-4 mb-4">
            <div className="bg-blue-500 text-white px-6 py-2 rounded-full font-bold text-lg">HEAR</div>
          </div>
          {step.instruction && (
            <p className="text-gray-700 text-lg max-w-2xl mx-auto">{step.instruction}</p>
          )}
        </div>
        <button
          onClick={handleStart}
          className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
        >
          Start
        </button>
      </div>
    );
  }

  return (
    <div className="virtual-class-page">
      {isRecording && (
        <div className="fixed top-20 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 font-bold text-lg" style={{ minWidth: '120px', textAlign: 'center' }}>
          {(config.recordingTimeLimit || 15) - recordingTime}s
        </div>
      )}
      
      <div className="mb-2 flex items-center gap-2">
        <div className="bg-blue-500 text-white px-4 py-1.5 rounded-full font-bold text-sm">
          HEAR
        </div>
        {step.instruction && (
          <p className="text-gray-700 text-sm">{step.instruction}</p>
        )}
      </div>

      {step.introductionAudio && (
        <div className="mb-2 scale-90 origin-top-left">
          <IntroductionAudio
            audio={step.introductionAudio}
            autoPlay={step.introductionAudio.autoPlay}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-2 mb-2">
            <h3 className="font-bold text-gray-800 mb-2 text-sm">Flashcards</h3>
            <div className="space-y-1.5">
              {flashcards.map((flashcard, index) => {
                const isCurrent = index === currentFlashcardIndex;
                const isCompleted = completedFlashcards.has(flashcard.id);
                const isAttempted = attemptedFlashcards.has(flashcard.id);
                const canClick = canClickFlashcard(index);
                const isSelected = selectedFlashcard?.id === flashcard.id;
                const result = flashcardResult[flashcard.id];
                const hasReminder = getReminderAudio(flashcard);
                
                return (
                  <div key={flashcard.id} className="relative">
                    <button
                      onClick={() => handleFlashcardClick(flashcard, index)}
                      className={`w-full p-2 text-left border-2 rounded-lg transition-all flex items-center justify-between ${
                        result === 'correct'
                          ? 'border-green-500 bg-green-50'
                          : result === 'incorrect'
                          ? 'border-red-500 bg-red-50'
                          : isSelected
                          ? 'border-red-500 bg-red-50'
                          : isCompleted
                          ? 'border-green-500 bg-green-50 opacity-60'
                          : !canClick
                          ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                          : 'border-gray-300 bg-white hover:bg-gray-50'
                      }`}
                      disabled={!canClick}
                    >
                      <span className="text-lg font-bold">{flashcard.word}</span>
                      {hasReminder && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            playReminderAudioHook(getReminderAudio(flashcard), () => {});
                          }}
                          className="ml-2 p-1.5 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                          title="Play reminder audio"
                        >
                          <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4-3.617a1 1 0 011.617.793zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" />
                          </svg>
                        </button>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div>
          <div className="bg-white border border-gray-300 rounded-lg p-2 mb-2">
            <h3 className="font-bold text-gray-800 mb-2 text-sm">Students</h3>
            
            <div className="relative mb-2 flashcard-container">
              <img
                src={page.studentsImage || aiStudentsImage}
                alt="Virtual Students"
                className={`w-full h-auto max-h-64 object-cover rounded-lg shadow-sm ${
                  borderAnimation === 'green' 
                    ? 'border-4 border-green-500' 
                    : borderAnimation === 'red'
                    ? 'border-4 border-red-500'
                    : 'border-4 border-transparent'
                }`}
                style={{
                  animation: borderAnimation === 'green' 
                    ? 'borderPulseGreen 3s ease-in-out' 
                    : borderAnimation === 'red'
                    ? 'borderPulseRed 3s ease-in-out'
                    : 'none'
                }}
              />
              <div 
                className="absolute top-1/2 left-1/2"
                style={{ 
                  transform: 'translate(-50%, -35%)',
                  minWidth: '220px',
                  minHeight: '140px'
                }}
              >
                {selectedFlashcard ? (
                  <div 
                    className="bg-white border-4 border-gray-300 rounded-lg p-6 text-center shadow-lg"
                    style={{ 
                      minWidth: '220px',
                      minHeight: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <p 
                      className="text-4xl font-bold"
                      style={{ 
                        color: '#1f2937',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {selectedFlashcard.word}
                    </p>
                  </div>
                ) : (
                  <div 
                    className="bg-white border-4 border-gray-300 rounded-lg p-6 text-center shadow-lg"
                    style={{ 
                      minWidth: '220px',
                      minHeight: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.7
                    }}
                  >
                    <p className="text-gray-400 text-sm">Click a word to show it here</p>
                  </div>
                )}
              </div>
            </div>

            {selectedFlashcard && (
              <div className="mt-2 text-center">
                {isProcessing ? (
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-2"></div>
                    <p className="text-xs font-medium text-gray-700">Processing...</p>
                  </div>
                ) : (
                  <button
                    onClick={isRecording ? handleSubmitRecording : handleStartRecording}
                    disabled={isProcessing || isPlayingReminder}
                    className={`
                      w-20 h-20 rounded-full flex items-center justify-center mx-auto transition-all shadow-lg
                      ${isRecording 
                        ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                        : isProcessing || isPlayingReminder
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                      }
                      text-white
                    `}
                    title={isRecording ? 'Stop Recording' : 'Start Recording'}
                  >
                    {isProcessing ? (
                      <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4z" />
                        <path d="M5.5 9.643a.75.75 0 00-1.5 0V10c0 3.06 2.29 5.585 5.25 5.954V17.5h-1.5a.75.75 0 000 1.5h4.5a.75.75 0 000-1.5h-1.5v-1.546A6.001 6.001 0 0016 10v-.357a.75.75 0 00-1.5 0V10a4.5 4.5 0 01-9 0v-.357z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showStepResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">
              {stepPassed ? 'Step Complete!' : 'Step Results'}
            </h2>
            <p className="text-gray-700 mb-4">
              {stepPassed 
                ? `Great job! You passed with ${Math.round((Array.from(passedFlashcards).length / flashcards.length) * 100)}% correct.`
                : `You got ${Array.from(passedFlashcards).length} out of ${flashcards.length} correct. ${stepAttempt < 2 ? 'You can try again!' : 'You can still continue to the next step.'}`}
            </p>
            <div className="flex gap-4">
              {stepPassed ? (
                <button
                  onClick={handleMoveToNext}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Continue
                </button>
              ) : (
                <>
                  {stepAttempt < 2 ? (
                    <button
                      onClick={handleRetry}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Try Again
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleRetry}
                        className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400"
                      >
                        Retry
                      </button>
                      <button
                        onClick={() => {
                          // Force enable next button
                          if (onCompletionChange) {
                            onCompletionChange(true);
                          }
                          handleMoveToNext();
                        }}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Continue Anyway
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HearPage;

